// import packages or dependencies
import React, { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import { editFormSetFormAction, resetCreateUserAction, userManagement } from "../userManagementSlice";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
  SvgIcon,
  Typography,
  Skeleton,
  useTheme,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { ReactComponent as TrashIcon } from "../../../../assets/icons/trash2-icon.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/arrow-right-icon.svg";
import {
  deleteModalOpenAction,
  deleteModalSetActionPathAction,
  deleteModalSetDeleteIdAction,
} from "../../../global/delete-modal/deleteModalSlice";
import { getCookie } from "../../../../configs/cookie-helper/cookieHelper";
import { useSnackbar } from "notistack";

// custom styles for mui components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: "14px",
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.text.primary,
    padding: "2px 2px 2px 24px",
    height: "65px",
    borderBottom: "none",
    [theme.breakpoints.down("lg")]: {
      height: "60px",
      fontSize: "12px",
      padding: "2px 2px 2px 14px",
    },
    [theme.breakpoints.down("md")]: {
      height: "55px",
      fontSize: "10px",
      padding: "2px 2px 2px 4px",
    },
  },
  [`&.${tableCellClasses.body}`]: {
    maxWidth: 100,
    fontSize: "14px",
    padding: "2px 2px 2px 24px",
    height: "65px",
    borderBottom: "none",
    color: "inherit",
    [theme.breakpoints.down("lg")]: {
      height: "60px",
      fontSize: "12px",
      padding: "2px 2px 2px 14px",
    },
    [theme.breakpoints.down("md")]: {
      height: "55px",
      fontSize: "10px",
      padding: "2px 2px 2px 4px",
    },
  },
}));

// custom styles for mui components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.background.paper,
  },
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.background.paper,
    filter: "brightness(95%)",
  },
  color: "#A1A2A3",
  transition: "background 0.3s",
  "&:hover": {
    backgroundColor: "#e1e1e1",
    color: "inherit",
  },
}));

// custom styles for mui components
const useStyles = makeStyles((theme) => ({
  tableBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
  tableRoot: {
    "& th:last-child": {
      borderTopRightRadius: "8px",
    },
    "& tr:last-child": {
      borderRadius: "8px",
    },
    "& th:first-child": {
      borderTopLeftRadius: "8px",
    },
  },
  linkBox: {
    "&::-webkit-scrollbar": {
      borderRadius: "10px",
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      backdropFilter: "blur(4px)",
      backgroundColor: "rgba(28, 33, 45, 0.4)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

// skeleton instance array
const skeletonItems = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/**
 * @returns Displays the table related to the list of users in user management
 */
const UsersTable = () => {
  // states and handlers
  const classes = useStyles();

  const theme = useTheme();

  const username = getCookie("username");

  const rule = getCookie("rule");

  const { userManagementData, createUser: { actionStatus, alert }, } = useAppSelector(userManagement);

  const dispatch = useAppDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const [sort, setSort] = useState({
    nickName: "right",
    userName: "right",
    rule: "right",
  });

  // useEffect(() => {
  //   if (!!token) {
  //     dispatch(getUserManagementListAction());
  //   }
  // }, [dispatch, token]);

  useEffect(() => {
    if (actionStatus === "success" && alert.error === false) {
      enqueueSnackbar(
        alert.message === "insert_account"
          ? "New user created successfuly"
          : alert.message,
        {
          variant: "success",
        }
      );
    } else if (actionStatus === "rejected" || alert.error === true && alert.message === "Sorry, we encountered an error in delete method!") {
      enqueueSnackbar(
        alert.message === "username_not_unique"
          ? "Username should be unique"
          : alert.message === "not_match_old_Password"
            ? "Password or Old Password not match"
            : alert.message,
        {
          variant: "error",
        }
      );
    }
    dispatch(resetCreateUserAction())
  }, [
    actionStatus,
    dispatch,
    enqueueSnackbar,
    alert,
  ]);


  const sortHandler = (name: string) => {
    switch (name) {
      case "nickName":
        setSort((last) => ({
          ...last,
          nickName:
            last.nickName === "right"
              ? "down"
              : last.nickName === "down"
                ? "up"
                : last.nickName === "up"
                  ? "right"
                  : "",
          userName: "right",
          rule: "right",
        }));
        break;
      case "userName":
        setSort((last) => ({
          ...last,
          userName:
            last.userName === "right"
              ? "down"
              : last.userName === "down"
                ? "up"
                : last.userName === "up"
                  ? "right"
                  : "",
          nickName: "right",
          rule: "right",
        }));
        break;
      case "rule":
        setSort((last) => ({
          ...last,
          rule:
            last.rule === "right"
              ? "down"
              : last.rule === "down"
                ? "up"
                : last.rule === "up"
                  ? "right"
                  : "",
          nickName: "right",
          userName: "right",
        }));
        break;
      default:
        break;
    }
  };

  const sortSvgIcon = (sort: string) => {
    switch (sort) {
      case "down":
        return "rotate(90deg)";
      case "up":
        return "rotate(-90deg)";
      case "right":
        return "rotate(0deg)";
      default:
        return;
    }
  };

  const sortFunction = (
    items: any,
    variableItem: string,
    direction: string
  ) => {
    let firstSort;
    let secondSort;

    switch (direction) {
      case "a-z":
        firstSort = [...items].sort((a: any, b: any) => {
          const x = a[`${variableItem}`];
          const y = b[`${variableItem}`];

          return x < y ? -1 : x > y ? 1 : 0;
        });

        secondSort = [...firstSort].sort((a: any, b: any) => {
          const x = a[`${variableItem}`].toLowerCase();
          const y = b[`${variableItem}`].toLowerCase();

          return x < y ? -1 : x > y ? 1 : 0;
        });

        return secondSort;

      case "z-a":
        firstSort = [...items].sort((a: any, b: any) => {
          const x = a[`${variableItem}`];
          const y = b[`${variableItem}`];

          return x < y ? 1 : x > y ? -1 : 0;
        });

        secondSort = [...firstSort].sort((a: any, b: any) => {
          const x = a[`${variableItem}`].toLowerCase();
          const y = b[`${variableItem}`].toLowerCase();

          return x < y ? 1 : x > y ? -1 : 0;
        });

        return secondSort;

      default:
        return items;
    }
  };

  const sortItems = (items: any) => {
    if (sort.nickName === "down") {
      return sortFunction(items, "nickname", "a-z");
    }

    if (sort.nickName === "up") {
      return sortFunction(items, "nickname", "z-a");
    }

    if (sort.userName === "down") {
      return sortFunction(items, "username", "a-z");
    }

    if (sort.userName === "up") {
      return sortFunction(items, "username", "z-a");
    }

    if (sort.rule === "down") {
      const admins = [...items].filter((item: any) => item.rule === "1");
      const users = [...items].filter((item: any) => item.rule === "2");

      return [...admins, ...users];
    }
    if (sort.rule === "up") {
      const users = [...items].filter((item: any) => item.rule === "2");
      const admins = [...items].filter((item: any) => item.rule === "1");

      return [...users, ...admins];
    }

    return items;
  };

  // render jsx
  return (
    <TableContainer
      data-testid="users_table_main_container"
      component={Box}
      sx={{
        minHeight: "130px",
        maxHeight: "100%",
        borderRadius: "8px",
      }}
      className={classes.tableBox}
    >
      <Table
        data-testid="users_table_table"
        stickyHeader
        className={classes.tableRoot}
      >
        <TableHead>
          <TableRow>
            <StyledTableCell align="left" width="2%"></StyledTableCell>
            <StyledTableCell align="left" width="16%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  data-testid="svg_arrow_right_icon"
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.nickName),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("nickName")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Nickname
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="16%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  data-testid="svg_arrow_right_icon"
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.userName),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("userName")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Username
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="16%">
              <Box
                sx={{
                  display: "flex",
                }}
              >
                <SvgIcon
                  data-testid="svg_arrow_right_icon"
                  component={ArrowRightIcon}
                  inheritViewBox
                  sx={{
                    width: "14px",
                    height: "auto",
                    color: "inherit",
                    cursor: "pointer",
                    transform: sortSvgIcon(sort.rule),
                    transition: "all 0.4s",
                    mr: "8px",
                    [theme.breakpoints.down("lg")]: {
                      width: "12px",
                    },
                  }}
                  onClick={() => sortHandler("rule")}
                />

                <Typography
                  sx={{
                    fontSize: "inherit",
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  Role
                </Typography>
              </Box>
            </StyledTableCell>
            <StyledTableCell align="left" width="4%"></StyledTableCell>
            <StyledTableCell align="left" width="4%"></StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {userManagementData.actionStatus === "loading"
            ? skeletonItems.map((row: any, index: number) => (
              <StyledTableRow key={index}>
                <StyledTableCell align="left">
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{
                      width: "100%",
                      height: "20px",
                      bgcolor: "#e8e8e8",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{
                      width: "90%",
                      height: "20px",
                      bgcolor: "#e8e8e8",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{
                      width: "90%",
                      height: "20px",
                      bgcolor: "#e8e8e8",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{
                      width: "90%",
                      height: "20px",
                      bgcolor: "#e8e8e8",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{
                      width: "90%",
                      height: "20px",
                      bgcolor: "#e8e8e8",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Skeleton
                    variant="rounded"
                    animation="wave"
                    sx={{
                      width: "90%",
                      height: "20px",
                      bgcolor: "#e8e8e8",
                      [theme.breakpoints.down("md")]: {
                        height: "14px",
                      },
                    }}
                  />
                </StyledTableCell>
              </StyledTableRow>
            ))
            : sortItems(userManagementData?.list)?.map(
              (row: any, index: number) => (
                <StyledTableRow
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(
                      editFormSetFormAction({
                        type: "update",
                        userId: row.id,
                        formIsOpen: true,
                        formData: {
                          nickname: row.nickname,
                          username: row.username,
                          rule: row.rule === "1" ? "admin" : "user",
                          password: "",
                          passwordConf: "",
                          oldPass: "",
                        },
                      })
                    );
                  }}
                >
                  <StyledTableCell align="left" sx={{ cursor: "pointer" }}>
                    {index + 1}
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={{ cursor: "pointer" }}>
                    <Typography
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        height: "20px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "inherit",
                        color: "inherit",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    >
                      {row.nickname}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={{ cursor: "pointer" }}>
                    <Typography
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        height: "20px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "inherit",
                        color: "inherit",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    >
                      {row.username}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={{ cursor: "pointer" }}>
                    <Typography
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        height: "20px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        fontSize: "inherit",
                        color: "inherit",
                        [theme.breakpoints.down("md")]: {
                          height: "14px",
                        },
                      }}
                    >
                      {row.rule === "1"
                        ? "admin"
                        : row.rule === "2"
                          ? "user"
                          : null}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={{ cursor: "pointer" }}>
                    <Tooltip
                      arrow
                      placement="bottom"
                      title="Update User"
                      enterDelay={500}
                      enterNextDelay={1000}
                      leaveDelay={500}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            marginTop: "10px !important",
                          },
                        },
                      }}
                    >
                      <SvgIcon
                        data-testid="svg_edit_icon"
                        component={EditRoundedIcon}
                        inheritViewBox
                        sx={{
                          width: "24px",
                          height: "24px",
                          mr: "8px",
                          [theme.breakpoints.down("lg")]: {
                            width: "22px",
                            height: "22px",
                          },
                          [theme.breakpoints.down("md")]: {
                            width: "20px",
                            height: "20px",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(
                            editFormSetFormAction({
                              type: "update",
                              userId: row.id,
                              formIsOpen: true,
                              formData: {
                                nickname: row.nickname,
                                username: row.username,
                                rule: row.rule === "1" ? "admin" : "user",
                                password: "",
                                passwordConf: "",
                                oldPass: "",
                              },
                            })
                          );
                        }}
                      />
                    </Tooltip>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {username === row.username && rule === row.rule ? (
                      <></>
                    ) : (
                      <>
                        <Tooltip
                          arrow
                          placement="bottom"
                          title="Delete User"
                          enterDelay={500}
                          enterNextDelay={1000}
                          leaveDelay={500}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                marginTop: "10px !important",
                              },
                            },
                          }}
                        >
                          <SvgIcon
                            data-testid="svg_trash_icon"
                            component={TrashIcon}
                            inheritViewBox
                            sx={{
                              width: "auto",
                              height: "16px",
                              color: "#E36464",
                              cursor: "pointer",
                              [theme.breakpoints.down("lg")]: {
                                height: "14px",
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(deleteModalOpenAction({ type: "User" }));
                              dispatch(
                                deleteModalSetActionPathAction(
                                  "user-management"
                                )
                              );
                              dispatch(deleteModalSetDeleteIdAction(row.id));
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              )
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UsersTable;
